import React, { useEffect, useState } from "react";
import { IconBtn } from "../../../atom/IconBtn/IconBtn";
import { insertValuesIntoString } from "../../../constants/functions";
import { ChevronLeftArrow } from "../../../constants/icons";
import {
  DEFAULT_COUNTRY_CODE,
  RESEND_OTP_TIMING,
  SIGN_IN_LANDING_PAGE,
  SUCCESS_AUTH,
  OTP_SEND_LIMIT
} from "../../../constants/variables";
import { useAuth } from "../../../context/AuthContext";
import { extractResource } from "../../../helper/extractResource";
import { Button } from "../../UI/Button";

interface SignInOTPPageProps {
  resourceData: Record<string, string>;
  setOTP: React.Dispatch<React.SetStateAction<string>>;
  emailOrMobile: string;
  OTP: string;
  handleState: React.SetStateAction<string>;
}

function SignInOTPPage({ resourceData, setOTP, OTP, emailOrMobile, handleState }: SignInOTPPageProps) {
  const [OTPError, setOTPError] = React.useState(false);
  const [timer, setTimer] = React.useState(RESEND_OTP_TIMING);
  const [isLoading, setIsLoading] = React.useState(false);
  const { verifyOtp, sendOtp } = useAuth();
  const [OTPLimit, setOTPLimit] = useState<number>(OTP_SEND_LIMIT);

  // ? Handle OTP's value on input change
  const handleChange = (e: { target: { value: React.SetStateAction<string> } }) => {
    setOTP(e.target.value);
  };

  /** creates a recurring function which updates the timer every one second
   *  until timer is reduced to 0
   * */
  const runTimer = () => {
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev === 0) {
          clearInterval(interval);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsLoading(true); // begin the loading animation

    // authenticate the OTP here
    if (OTP) {
      await verifyOtp(OTP).then((resp) => {
        if (resp) {
          setOTPError(false);
          handleState(SUCCESS_AUTH); // redirect to success page
        } else {
          setOTPError(true);
          setIsLoading(false);
        }
      });
    } else {
      setOTPError(true);
      setIsLoading(false);
    }
  };

  const resendOtp = async () => {
    // setIsLoading(true); //begin the loading animation
    await sendOtp(DEFAULT_COUNTRY_CODE + emailOrMobile).then(() => {
      setTimer(RESEND_OTP_TIMING);
      runTimer();
      setOTPLimit(pre=> pre - 1);
    });
  };

  /** calls the runTimer function after the page is loaded
   * so as to ensure the timer begins only after the page is fully loaded
   *  */
  useEffect(() => {
    runTimer();
  }, []);

  // ? returns sign in module's OTP screen
  return (
    <section className="w-full sm:w-80 m-auto h-full flex flex-col justify-between">
      <div className="w-full h-fit">
        <div className="flex items-center text-typography-primary">
          <IconBtn
            onClick={() => {
              handleState(SIGN_IN_LANDING_PAGE);
            }}
            aria-label={extractResource(resourceData, "goBack")}
            title={extractResource(resourceData, "goBack")}
            type="button"
            edge="start"
          >
            <ChevronLeftArrow fill="inherit" />
          </IconBtn>
          <span className="font-semibold text-xl mx-3">{extractResource(resourceData, "enterOtp")}</span>
        </div>

        <form onSubmit={handleSubmit} name="signInWithOTP">
          <input
            type="number"
            placeholder="Enter OTP"
            className={`tw-input pl-5 rounded-lg rounded-tr-none border ${
              OTPError ? "border-typography-error mt-6" : "border-typography-secondary mt-6 mb-5"
            } w-full h-12 otp`}
            value={OTP}
            name="otp"
            onChange={handleChange}
          />

          <p className="text-typography-error text-xs my-1 mx-3">
            {OTPError ? extractResource(resourceData, "invalidOtp") : ""}
          </p>

          <Button
            color="primary"
            variant="contained"
            type="submit"
            fullWidth
            aria-label={extractResource(resourceData, "confirmAndSignIn")}
            isLoading={isLoading}
            disabled={isLoading}
          >
            {extractResource(resourceData, "confirmAndSignIn")}
          </Button>

          <div>
            <div className="flex my-8">
              {timer > 0 ? (
                <div className="m-auto font-semibold w-fit text-center flex my-8">
                  <span className="text-typography-secondary dark:text-white mr-1">
                    {extractResource(resourceData, "resendOtp")}
                  </span>
                  <span>{insertValuesIntoString(extractResource(resourceData, "inDuration"), [timer])}</span>
                </div>
              ) : (
                <Button
                  fullWidth
                  onClick={() => resendOtp()}
                  variant="text"
                  color="secondary"
                  aria-label={extractResource(resourceData, "resendOtp")}
                  type="button"
                  disabled={OTPLimit === 0}
                >
                  {extractResource(resourceData, "resendOtp")}
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>
      <div className="w-full text-center relative left-0">
        <Button
          color="secondary"
          variant="text"
          className="w-fit m-auto"
          onClick={() => {
            // sets the state to landingPage in order to go to landing screen
            handleState(SIGN_IN_LANDING_PAGE);
          }}
          type="button"
          aria-label={extractResource(resourceData, "otherSignInOptions")}
        >
          {extractResource(resourceData, "otherSignInOptions")}
        </Button>
      </div>
    </section>
  );
}

export default SignInOTPPage;
